"use client";

// THE BUTTON THAT SIGNS SOMEONE IN, AND THEN LOADS THE APP AS A DOCUMENT
// (fine only if a first sign-in never looks hung).
//
// Used by the two sign-ins that spend a credential on this site: the email
// code (/signin) and the Slack link's Continue (/login/continue). Slack's own
// OAuth button is not one of them: it leaves for slack.com, and comes back
// through a 302 that is already a document load.
//
// The action answers `{ landing }` (lib/server/sign-in-landing.ts), and this
// calls `location.assign(landing)`: a document load, so the first page arrives
// with its route's seed instead of an empty shell that StoreSync fills a
// second later. A failure is still the action's redirect() back to its page,
// with its sentence, as before.
//
// From the press until the next page paints, the button reads *Signing you
// in…*, disabled, and the form's fields with it. `pending` covers the action;
// `leaving` covers the document load after it, which keeps this page on screen
// until the new one is ready (and is never reset, because this page is going
// away; a Back that brings it out of the back/forward cache resets it).
//
// ⚠️ onSubmit, NOT <form action={fn}>. React resets a form after a function
// action, which would empty the code box under *Signing you in…*. Calling
// preventDefault here tells React not to run the form's action (react-dom's
// form-action plugin checks `defaultPrevented`), and the transition started
// below still drives useFormStatus. The form's `action` is still the server
// action, so a press before hydration is a plain POST that lands as a document
// too (sign-in-landing.ts).
//
// ⚠️ A CAUGHT FAILURE IS A SENTENCE, NEVER THE ERROR SCREEN. The action
// rejects when the connection drops or the server fails, and on a tab older
// than a release (UnrecognizedActionError). `caughtActionText` gives the last
// its true sentence; anything else reads as the page's own `Configuration`
// failure. A redirect() is the action's answer and is rethrown for Next.

import { useEffect, useState, useTransition } from "react";
import { unstable_rethrow } from "next/navigation";
import { Loader2 } from "lucide-react";
import { caughtActionText } from "@/lib/stale-action";
import type { SignInLanding } from "@/lib/server/sign-in-landing";

export const SIGNING_IN_TEXT = "Signing you in…";
export const SIGN_IN_FAILED_TEXT = "Sign-in didn’t finish. Try again in a minute.";

/** The move after a successful sign-in, a seam for the unit test. */
export const signInNavigation = {
  assign(href: string) {
    window.location.assign(href);
  },
};

export function SignInForm({
  action,
  label,
  className,
  buttonClassName,
  testid,
  children,
}: {
  action: (form: FormData) => Promise<SignInLanding>;
  label: string;
  className?: string;
  buttonClassName: string;
  testid: string;
  children?: React.ReactNode;
}) {
  const [pending, startTransition] = useTransition();
  const [leaving, setLeaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const busy = pending || leaving;

  // A Back to this page from the back/forward cache brings it back as it was
  // left: mid-sign-in. Without this, the button would read *Signing you in…*
  // forever on a page that is doing nothing.
  useEffect(() => {
    const restored = (e: PageTransitionEvent) => {
      if (e.persisted) setLeaving(false);
    };
    window.addEventListener("pageshow", restored);
    return () => window.removeEventListener("pageshow", restored);
  }, []);

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    const form = new FormData(e.currentTarget);
    setFailed(null);
    startTransition(async () => {
      try {
        const { landing } = await action(form);
        setLeaving(true);
        signInNavigation.assign(landing);
      } catch (err) {
        unstable_rethrow(err);
        setFailed(caughtActionText(err, SIGN_IN_FAILED_TEXT));
      }
    });
  }

  return (
    <>
      {/* The cast: React's typing wants a form action to return nothing, and
          this one returns the landing. React ignores the value either way. */}
      <form action={action as unknown as (form: FormData) => Promise<void>} onSubmit={onSubmit} className={className}>
        <fieldset disabled={busy} className="contents">
          {children}
          <button type="submit" className={buttonClassName} data-testid={testid} aria-busy={busy}>
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={14} aria-hidden="true" className="animate-spin" />
                {SIGNING_IN_TEXT}
              </span>
            ) : (
              label
            )}
          </button>
        </fieldset>
      </form>
      {failed && (
        <p role="alert" className="text-[12px] text-red-400 max-w-xs text-center" data-testid="signin-failed">
          {failed}
        </p>
      )}
    </>
  );
}
